import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Plus, X } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { useBrandOnboarding } from "@/context/BrandOnboardingContextFixed";
import { useDebouncedNavigation } from "@/hooks/use-debounced-navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import logo from '../../assets/images/cup-artie.png';

const SOCIAL_PLATFORMS = ["Instagram", "TikTok", "YouTube", "Twitter", "LinkedIn", "Facebook", "Other"];
const BUDGET_RANGES = ["50k_250k", "250k_1m", "1m_5m", "over_5m"];

const brandProfileSchema = z.object({
  brand_division: z.string().max(100, "Division must be 100 characters or less").optional(),
  function_role: z.string().min(2, "Role is required").max(100, "Role must be 100 characters or less"),
  work_location: z.string().min(2, "Location is required").max(100, "Location must be 100 characters or less"),
  annual_marketing_budget: z.enum(BUDGET_RANGES as [string, ...string[]]).optional(),
  annual_creator_budget: z.enum(BUDGET_RANGES as [string, ...string[]]).optional(),
  referral_source: z.string().optional(),
  signup_reason: z.string().min(10, "Please provide at least 10 characters").max(500, "Please keep your response under 500 characters").optional(),
  brand_social_channels: z.array(z.object({
    platform: z.string(),
    handle: z.string(),
    is_primary: z.boolean(),
  })).optional(),
});

type BrandProfileFormValues = z.infer<typeof brandProfileSchema>;

export default function BrandOnboardingStep2Fixed() {
  const [isLoading, setIsLoading] = useState(false);
  const [socialChannels, setSocialChannels] = useState<{platform: string, handle: string, is_primary: boolean}[]>([]);
  const navigate = useNavigate();
  const { status, isLoading: isStatusLoading, refreshStatus } = useBrandOnboarding();

  // Use debounced navigation to prevent glitches
  useDebouncedNavigation(
    status?.onboarding_status,
    isStatusLoading,
    [
      {
        condition: status?.onboarding_status === "account_created",
        path: "/brand-onboarding/company-info",
        delay: 600
      },
      {
        condition: status?.onboarding_status === "brand_profile_completed",
        path: "/brand-onboarding/complete",
        delay: 600
      },
      {
        condition: status?.onboarding_status === "onboarding_complete",
        path: "/brand-dashboard",
        delay: 600
      }
    ]
  );

  const form = useForm<BrandProfileFormValues>({
    resolver: zodResolver(brandProfileSchema),
    defaultValues: {
      brand_division: "",
      function_role: "",
      work_location: "",
      annual_marketing_budget: undefined,
      annual_creator_budget: undefined,
      referral_source: "",
      signup_reason: "",
      brand_social_channels: [],
    },
  });

  const addSocialChannel = () => {
    setSocialChannels([...socialChannels, { platform: "Instagram", handle: "", is_primary: false }]);
  };

  const removeSocialChannel = (index: number) => {
    const newChannels = [...socialChannels];
    newChannels.splice(index, 1);
    setSocialChannels(newChannels);
  };

  const updateSocialChannel = (index: number, field: string, value: any) => {
    const newChannels = [...socialChannels];
    newChannels[index] = { ...newChannels[index], [field]: value };
    setSocialChannels(newChannels);
  };

  async function onSubmit(data: BrandProfileFormValues) {
    setIsLoading(true);
    
    console.log("🚀 BrandOnboardingStep2: Form submission started", {
      timestamp: new Date().toISOString()
    });
    
    try {
      console.log("🌐 BrandOnboardingStep2: Making API call to setupBrandProfile");
      await apiClient.setupBrandProfile({
        ...data,
        brand_social_channels: socialChannels,
      });
      console.log("✅ BrandOnboardingStep2: setupBrandProfile successful");

      toast.success("Brand profile saved!");
      
      // Add delay before refreshing status to prevent race conditions
      console.log("🔄 BrandOnboardingStep2: Refreshing status after delay");
      setTimeout(async () => {
        await refreshStatus();
        console.log("✅ BrandOnboardingStep2: Status refresh completed");
      }, 300);
      
      // Navigation is now handled by useDebouncedNavigation hook
      console.log("✅ BrandOnboardingStep2: Form submission completed, navigation handled by hook");
    } catch (error) {
      console.error("❌ BrandOnboardingStep2: Form submission failed", error);
      toast.error(error instanceof Error ? error.message : "Failed to save brand profile");
    } finally {
      setIsLoading(false);
      console.log("🏁 BrandOnboardingStep2: Form submission completed");
    }
  }

  return (
    <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">

      <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

        <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
          {/* Logo */}
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
            <img
              src={logo}
              alt="Artie"
              className="w-48 h-48 object-contain relative z-10"
            />
          </div>

          {/* Header Section */}
          <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
              Tell us about your brand
            </h1>
            <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
              Help us understand your brand better so we can match you with the perfect creators.
            </p>
          </div>

          {/* Main Form Container */}
          <div className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] rounded-2xl p-8 md:p-10 animate-in slide-in-from-bottom-4 duration-500 delay-100">

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="function_role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Your Role</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Marketing Manager"
                            {...field}
                            className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="brand_division"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Division (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g. North America, Global"
                            {...field}
                            className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="work_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Work Location</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="City, Country"
                          {...field}
                          className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="annual_marketing_budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Annual Marketing Budget</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]">
                              <SelectValue placeholder="Select budget range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="50k_250k">$50k–250k</SelectItem>
                            <SelectItem value="250k_1m">$250k–1M</SelectItem>
                            <SelectItem value="1m_5m">$1M–5M</SelectItem>
                            <SelectItem value="over_5m">Over $5M</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="annual_creator_budget"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Creator/Influencer Budget</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="h-12 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]">
                              <SelectValue placeholder="Select budget range" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="50k_250k">$50k–250k</SelectItem>
                            <SelectItem value="250k_1m">$250k–1M</SelectItem>
                            <SelectItem value="1m_5m">$1M–5M</SelectItem>
                            <SelectItem value="over_5m">Over $5M</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Social Channels Section */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <FormLabel className="text-base text-[oklch(0.2686_0_0)] font-medium">Brand Social Channels</FormLabel>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSocialChannel}
                      className="rounded-xl border-[oklch(0.92_0_0)] hover:bg-[oklch(0.97_0.01_60)]"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Channel
                    </Button>
                  </div>
                  
                  {socialChannels.map((channel, index) => (
                    <div key={index} className="flex gap-4 items-end bg-[oklch(0.97_0.01_60)] p-4 rounded-xl border border-[oklch(0.92_0_0)]">
                      <div className="flex-1 space-y-2">
                        <label className="text-xs font-medium text-[oklch(0.55_0_0)]">Platform</label>
                        <Select
                          value={channel.platform}
                          onValueChange={(val) => updateSocialChannel(index, "platform", val)}
                        >
                          <SelectTrigger className="h-10 rounded-xl border-[oklch(0.92_0_0)]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SOCIAL_PLATFORMS.map(p => (
                              <SelectItem key={p} value={p}>{p}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex-[2] space-y-2">
                        <label className="text-xs font-medium text-[oklch(0.55_0_0)]">Handle / URL</label>
                        <Input
                          value={channel.handle}
                          onChange={(e) => updateSocialChannel(index, "handle", e.target.value)}
                          placeholder="@username or https://..."
                          className="h-10 rounded-xl border-[oklch(0.92_0_0)]"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeSocialChannel(index)}
                        className="text-[oklch(0.55_0_0)] hover:text-red-500 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {socialChannels.length === 0 && (
                    <p className="text-sm text-[oklch(0.55_0_0)] italic">No social channels added yet.</p>
                  )}
                </div>

                <FormField
                  control={form.control}
                  name="signup_reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-[oklch(0.2686_0_0)] font-medium">Why are you signing up?</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Tell us what you're looking to achieve with Artie..."
                          className="resize-none h-24 rounded-xl border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)]"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
  
              </form>
            </Form>
          </div>
        </div>

        {/* Footer & Navigation */}
        <div className="w-full mt-auto pb-10 px-6 md:px-20">
          <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
          <div className="w-full flex items-center justify-between">
            <div className="flex-1 flex justify-start">
              {/* Back button removed to prevent navigation loop */}
            </div>

            {/* Progress Indicators */}
            <div className="flex gap-2 justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
              <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
            </div>

            <div className="flex-1 flex justify-end">
              <Button
                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={form.handleSubmit(onSubmit)}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Continue"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}